import type { PaletteColor } from "@brexhq/metal-foundation";
import { PortableText, PortableTextProps } from "@portabletext/react";
import React, { PropsWithChildren } from "react";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { buildLinkPath } from "@brexhq/next/src/components/pageBuilder/utils";
import { Link } from "@brexhq/next/src/components/shared/Link/Link";
import {
  BodyL,
  BodyM,
  BodyS,
  Detail,
  TitleS,
  TitleM,
  DisplayL,
} from "@brexhq/shared/components/Typography/Components";
import {
  BulletList,
  NumberedList,
  ListItem,
  Span,
} from "@brexhq/shared/components/Typography/Typography";
import type {
  MarginOptions,
  ListProps,
} from "@brexhq/shared/components/Typography/types";
import { defaultTheme } from "@brexhq/shared/styles/theming";
import { ModuleTheme, ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";

import CopyLibraryItem from "../../shared/BlockRenderer/CopyLibraryItem";
import { ManifestoInlineCta } from "../../shared/ManifestoInlineCta/ManifestoInlineCta";
import { TypingAnimation } from "../Typography/TypingAnimation";

/** Sometimes type is defined as body small and sometimes BodySmall - there is logic in this file to account for both for now but we should try and figure out why / how there has become a mismatch and patch it appropriately */

interface BlockContentOptions {
  marks?: {
    [key: string]: (value: { mark: string; children: any }) => JSX.Element;
  };
  types?: {
    [key: string]: (block: { children: any; value: any }) => JSX.Element;
  };
  // The fontsize of the list
  listType?: ListProps["type"];
}

const ListRenderer: (listType?: ListProps["type"]) => React.FC<any> = (
  listType = "BodyLarge",
) =>
  function ListRenderer(props) {
    return props.type === "number" ? (
      <NumberedList type={listType}>{props.children}</NumberedList>
    ) : (
      <BulletList type={listType}>{props.children}</BulletList>
    );
  };

// TODO How to do away with the linebreak in a way thats consistent with current typography system
const NoStyles: React.FC<PropsWithChildren> = ({ children }) => (
  <>
    {children}
    <br />
  </>
);

const componentMap = {
  BodySmall: BodyS,
  Headline: TitleS,
  Title: DisplayL,
  BodyLarge: BodyL,
  Subtitle: TitleM,
  BodyM: BodyM,
  Detail: Detail,
  None: NoStyles,
} as const;

type BlockContentComponents = keyof typeof componentMap;

const generateSerializers = (
  defaultStyleRenderer: BlockContentComponents,
  color: { [Key in BlockContentComponents]?: PaletteColor },
  textMargin: { [Key in BlockContentComponents]?: MarginOptions },
  options: BlockContentOptions,
  decodedModuleTheme: ModuleTheme,
  renderChildAsSpan?: boolean,
  isInView?: boolean,
): PortableTextProps["components"] => {
  const NormalText = componentMap[defaultStyleRenderer];
  const { textSecondary, textPrimary } = decodedModuleTheme.standard;
  return {
    marks: {
      link: ({ value, children }) => {
        const to = buildLinkPath({ link: value?.link });
        return to ? (
          <Link
            component={""}
            modifier="inline"
            to={to}
            label={children?.[0]}
          />
        ) : (
          children?.[0]
        );
      },
      black: ({ children }) => (
        <Span color={defaultTheme.colors.content.primary}>{children}</Span>
      ),
      gray: ({ children }) => (
        <Span color={defaultTheme.colors.content.tertiary}>{children}</Span>
      ),
      em: ({ children }) => <em>{children}</em>,
      sup: ({ children }) => <sup>{children}</sup>,
      strong: ({ children }) => <Span fontWeight={600}>{children}</Span>,
      hashId: ({ value, children }) => (
        <span id={value?.link?.hashId}>{children}</span>
      ),
      textPrimary: (props) => {
        return <Span color={textPrimary}>{props.children}</Span>;
      },
      typingAnimation: (props) => {
        return <TypingAnimation>{props.children}</TypingAnimation>;
      },
      ...(options.marks ?? {}),
    },
    block: {
      Headline: ({ children, value }) => (
        <TitleS
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </TitleS>
      ),
      Subtitle: ({ children, value }) => (
        <TitleM
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </TitleM>
      ),
      Title: ({ children, value }) => (
        <DisplayL
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </DisplayL>
      ),
      normal: ({ children }) => (
        <NormalText
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[defaultStyleRenderer ?? "None"]}
          color={color[defaultStyleRenderer ?? "None"]}
        >
          {children}
        </NormalText>
      ),
      BodyLarge: ({ children, value }) => (
        <BodyL
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </BodyL>
      ),
      BodySmall: ({ children, value }) => (
        <BodyS
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </BodyS>
      ),
      "body small": ({ children, value }) => (
        <BodyS
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </BodyS>
      ),
      none: ({ children, value }) => (
        <BodyS
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </BodyS>
      ),
      blockquote: ({ children, value }) => (
        <BodyM
          as={renderChildAsSpan ? "span" : undefined}
          margin={textMargin[value.style ?? "None"]}
          color={color[value.style ?? "None"]}
        >
          {children}
        </BodyM>
      ),
    },
    types: {
      copyLibraryReference: ({ value }) => {
        return <CopyLibraryItem value={value} />;
      },
      manifestoInlineCta: ({ value }) => {
        return <ManifestoInlineCta value={value} isInView={isInView} />;
      },
      ...(options.types ?? {}),
    },
    list: ListRenderer(options.listType),
    listItem: function ListItemComponent(props: React.PropsWithChildren<any>) {
      return (
        <ListItem
          type={
            props.value.style === "normal" ? "BodySmall" : props.value.style
          }
          color={textSecondary}
        >
          {props.children}
        </ListItem>
      );
    },
  };
};

//NOTE: I'd rather not need to do add the renderChildAsSpan prop, but its the source of lots of errors in Sentry and it comes down to invalid HTML. This solves the problem but not the root of the problem which is that when we are passing blocks into a BlockContent component that is wrapped in another Text component, the blocks should never have style data associated with them, which would always ensure they render with the NoStyles component

/**
 * Block content to render portable text from sanity Grid
 * @param defaultStyleRenderer  The component to use when Sanity returns `normal` as the style. None is the default.
 * @param blocks Data from sanity `_rawTextContent`
 * @param textMargin Text margin per component
 * @param color color per component
 * @param options custom marks, types and fontSizes
 * @param renderChildAsSpan used to render the child components as a span, rather then including p inside p
 *
 * @deprecated use BlockRenderer instead
 */
const BlockContent: React.FC<{
  moduleTheme?: ModuleThemeType;
  defaultStyleRenderer?: BlockContentComponents;
  blocks: Array<any>;
  color?: { [Key in BlockContentComponents]?: PaletteColor };
  textMargin?: { [Key in BlockContentComponents]?: MarginOptions };
  options?: BlockContentOptions;
  renderChildAsSpan?: boolean;
  isInView?: boolean;
}> = ({
  moduleTheme = "PrimaryWhite",
  blocks,
  color = {},
  textMargin = {},
  options = {},
  defaultStyleRenderer = "None",
  renderChildAsSpan,
  isInView,
}) => {
  const decodedModuleTheme = getModuleThemeColors(moduleTheme);
  const serializers = generateSerializers(
    defaultStyleRenderer,
    color,
    textMargin,
    options,
    decodedModuleTheme,
    renderChildAsSpan,
    isInView,
  );

  return <PortableText value={blocks} components={serializers} />;
};

export default BlockContent;
